import { useTheme } from "@mui/material";
import { FlagOutlined } from "@mui/icons-material";
import { StatsProps } from "~/components/DetailsPage/components/Stats/Stats";
import { Radar as RadarIcon } from "@mui/icons-material";
import { ReportType } from "~/reportApp/reportingApp";
import { useReportingPageLoaded } from "../../hooks/useReportingPageLoaded";
import { useFetchAggregateScoresRecursively } from "./useFetchAggregateScoresRecursively";
import { useMetrics } from "~/hooks";
import { Metrics } from "~/lib/arrow";
import { SmallDonutData } from "~/components/SmallDonut";
import { getColor } from "~/lib/colors";
import { pluralize } from "~/lib/pluralize";
import {
  AggregateScoreType,
  ScoreRating,
  useGetSpaceNameQuery,
} from "~/operations";
import { AggregateScoresEdge } from "~/pages/reports/SecurityReport/utils/securityReportTypes";
import { VulnerabilitiesIcon } from "~/icons";

type RadialDataItem = {
  id: string;
  value: number;
  color: string;
};

export function useChecksOrVulnerabilitiesReport({
  reportType,
}: {
  reportType: ReportType;
}) {
  let theme = useTheme();
  const { searchParams } = new URL(window.location.href);
  //   scope may be either a "space" or "workspace"
  const scopeMrn = searchParams.get("scopeMrn") || "";

  // Get space name only from space endpoint
  // THIS MAY NEED ADJUSTMENT TO HANDLE A WORKSPACE
  const { data: spaceData, networkStatus: spaceNetworkStatus } =
    useGetSpaceNameQuery({
      variables: {
        mrn: scopeMrn,
      },
    });

  // recursively gather all the checks in the space
  const { allItems: allChecks, networkStatus: fetchAllNetworkStatus } =
    useFetchAggregateScoresRecursively({
      scopeMrn,
      filter: {
        scoreType: AggregateScoreType.Check,
      },
      skip: reportType !== ReportType.Checks,
    });

  // recursively gather all the advisories in the space
  const {
    allItems: allAdvisories,
    networkStatus: fetchAllAdvisoriesNetworkStatus,
  } = useFetchAggregateScoresRecursively({
    scopeMrn,
    filter: {
      scoreType: AggregateScoreType.Advisory,
      minRiskValue: 1,
    },
    skip: reportType !== ReportType.Vulnerabilities,
  });

  // recursively gather all the Vulns in the space
  const { allItems: allVulns, networkStatus: fetchAllVulnsNetworkStatus } =
    useFetchAggregateScoresRecursively({
      scopeMrn,
      filter: {
        scoreType: AggregateScoreType.Vulnerability,
        minRiskValue: 1,
      },
      skip: reportType !== ReportType.Vulnerabilities,
    });

  // Get donut chart data from metrics => METRICS OFF
  // Metrics used only in vulnerabilities report
  const vulnMetrics = [Metrics.CVEsSeverity, Metrics.VulnerableAssetsSeverity];

  // Metrics used only in checks report
  const checksMetrics = [
    Metrics.SecurityAssetsStats, // for exposed assets on Checks report
  ];

  const scopedMetricMrns = () => {
    if (reportType === ReportType.Vulnerabilities) {
      return vulnMetrics;
    }

    if (reportType === ReportType.Checks) {
      return checksMetrics;
    }

    return [];
  };

  const {
    cvesSeverity,
    vulnerableAssetsSeverity,
    assetRiskRatings,
    networkStatus: metricsNetworkStatus,
  } = useMetrics({
    entityMrn: scopeMrn,
    metricMrns: [Metrics.AssetsRiskRatingDistribution, ...scopedMetricMrns()],
  });

  const networkStatuses = [
    spaceNetworkStatus,
    fetchAllAdvisoriesNetworkStatus,
    fetchAllVulnsNetworkStatus,
    fetchAllNetworkStatus,
    metricsNetworkStatus,
  ];

  // Determine if all network requests are ready
  useReportingPageLoaded(networkStatuses);

  const buildRadialDataFromMetric = (
    metricData: SmallDonutData,
  ): RadialDataItem[] => {
    const filteredOutRatings = ["none", "snoozed", "disabled"];

    return metricData.context
      .filter((md) => !filteredOutRatings.includes(md.rating))
      .map((md) => {
        return {
          id: md.rating,
          value: md.value,
          color: getColor(theme, md.rating),
        };
      });
  };

  // CAN DELETE AFTER SWITCH TO METRICS FOR CHECK RADIAL STATS
  const groupAggScoreEdgesByRating = (aggScoresAssetsEdges: any) => {
    const ratings = {
      CRITICAL: 0,
      HIGH: 0,
      MEDIUM: 0,
      LOW: 0,
      NONE: 0,
    };

    aggScoresAssetsEdges.forEach((edge: AggregateScoresEdge) => {
      const rating = edge.node?.rating;

      if (rating && ratings.hasOwnProperty(rating)) {
        ratings[rating as keyof typeof ratings]++;
      }
    });

    return Object.entries(ratings).map(([key, value], index) => {
      return {
        id: key,
        value,
        color: getColor(theme, key),
      };
    });
  };

  const getTotalFindingsCount = (): string => {
    if (reportType === ReportType.Vulnerabilities) {
      return (totalVulns + totalAdvisories).toString();
    }

    if (reportType === ReportType.Checks) {
      return totalFailingChecks.toString();
    }

    return "---";
  };

  const getExposedAssetCount = (): string => {
    if (reportType === ReportType.Vulnerabilities) {
      return getStatString(vulnerableAssetsSeverity.total);
    }

    if (reportType === ReportType.Checks) {
      const EXPOSED_GROUPINGS = ["critical", "high", "medium", "low"];
      const total = assetRiskRatings.context.reduce((ag, asset) => {
        if (EXPOSED_GROUPINGS.includes(asset.rating)) {
          ag += asset.value;
        }
        return ag;
      }, 0);

      return getStatString(total);
    }

    return "---";
  };

  const getPriorityVulns = (): number => {
    const PRIORITY_RATINGS = ["critical", "high"];
    return cvesSeverity.context.reduce((ag, v) => {
      if (PRIORITY_RATINGS.includes(v.rating)) {
        ag += v.value;
      }
      return ag;
    }, 0);
  };

  const getPriorityFindings = (
    failingChecks: AggregateScoresEdge[],
  ): number => {
    const PRIORITY_RATINGS = [ScoreRating.Critical, ScoreRating.High];
    const prios = failingChecks.filter((check) => {
      if (check.node?.rating) {
        return PRIORITY_RATINGS.includes(check.node?.rating);
      }
    });
    return prios.length || 0;
  };

  const getFailingChecks = (): AggregateScoresEdge[] => {
    return (
      allChecks?.filter((check) => (check.node?.riskValue ?? 100) > 0) || []
    );
  };

  const getStatString = (value: number): string => {
    return value < 0 ? "---" : value.toString();
  };

  //// CHECKS REPORT DATA
  const failingChecks = getFailingChecks();
  const totalFailingChecks = failingChecks.length;
  const priorityFindings = getPriorityFindings(failingChecks);
  const checksRadialData = groupAggScoreEdgesByRating(allChecks);

  //// VULNERABILITIES REPORT DATA
  const totalVulns = allVulns?.length || 0;
  const totalAdvisories = allAdvisories?.length || 0;
  const vulnerabilitiesRadialData = buildRadialDataFromMetric(cvesSeverity);

  //// DUAL REPORT DATA
  const spaceName = spaceData?.space?.name;
  // metric to use => "//metrics.api.mondoo.app/rating/assets/vulns"
  const totalScanned = getStatString(assetRiskRatings.total);
  //// use MetricSecurityAssetsStats for exposed assets on Checks report
  const totalExposed = getExposedAssetCount();
  const totalFindings = getTotalFindingsCount();
  const priorityVulns = getPriorityVulns();

  const checksGroupSummary = [
    {
      name: "Checks",
      description: `In the ${spaceName} space, Mondoo discovered ${totalFailingChecks} failed security policy checks across ${totalScanned} scanned assets. ${priorityFindings} ${pluralize("finding", priorityFindings)} have a critical or high risk rating.`,
      data: checksRadialData,
    },
  ];

  const vulnerabilitiesGroupSummary = [
    {
      name: "Vulnerabilities",
      description: `In the ${spaceName} space, Mondoo discovered ${totalVulns} ${pluralize("vulnerability", totalVulns)} across ${totalScanned} scanned assets, 6 of which were exposed. ${priorityVulns} ${pluralize("vulnerabilities", priorityVulns)} have a critical or high risk rating. This report provides details about the team's performance against SLAs and the individual vulnerabilities found.`,
      // data from //metrics.api.mondoo.app/rating/vulns"
      data: vulnerabilitiesRadialData,
    },
  ];

  const stats: StatsProps["stats"] = [
    {
      label: "Scanned",
      value: totalScanned,
      icon: <RadarIcon fontSize="inherit" />,
    },
    {
      label: "Findings",
      value: totalFindings,
      icon: <FlagOutlined fontSize="inherit" />,
    },
    {
      label: "Exposed",
      value: totalExposed,
      icon: <VulnerabilitiesIcon fontSize="inherit" />,
    },
  ].flatMap((stat) => (stat ? stat : []));

  const pageTitle = spaceName
    ? `${spaceName} - Space ${reportType} Report`
    : "";

  return {
    scopeMrn,
    title: pageTitle,
    stats,
    groupSummary:
      reportType === ReportType.Checks
        ? checksGroupSummary
        : vulnerabilitiesGroupSummary,
    totalScanned,
    ...(reportType === ReportType.Checks && {
      failingChecks: {
        total: failingChecks?.length || 0,
        items: failingChecks,
        heading: "Failing checks",
      },
    }),
    ...(reportType === ReportType.Vulnerabilities && {
      foundVulns: {
        total: totalVulns,
        items: allVulns,
        heading: "Found vulnerabilities",
      },
      foundAdvisories: {
        total: totalAdvisories,
        items: allAdvisories,
        heading: "Found advisories",
      },
    }),
  };
}
